<template>
  <v-app v-if="isAuthorized">
    <FeedbackWindow v-if="isIframeOptionEnabled && isAuthorized" />
    <Launcher
      v-if="isAuthorized"
      :participants="participants"
      :title="title"
      :title-image-url="titleImageUrl"
      :new-messages-count="newMessagesCount"
      :is-open="isChatOpen"
      :is-conversation-started="isConversationStarted"
      :close="closeChat"
      :open="openChat"
      :restart-chat="debouncedRestartChat"
      :restart-messenger="restartMessenger"
      :go-back="goBack"
      :show-emoji="false"
      :show-file="false"
      :show-typing-indicator="showTypingIndicator"
      :colors="colors"
      :always-scroll-to-bottom="alwaysScrollToBottom"
      :message-styling="messageStyling"
      :disable-user-list-toggle="true"
      @onType="handleOnType"
    >
    </Launcher>
    <div v-else class="bg">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="50"
        :style="{ top: '33%' }"
      ></v-progress-circular>
    </div>
  </v-app>
</template>
<script>
const Launcher = () => import('./components/Launcher.vue')
const FeedbackWindow = () => import('./components/FeedbackWindow.vue')
import * as actions from './store/action-types'
import * as mutations from './store/mutation-types'
import {
  vueAppTitle,
  enableIframeOption,
  disableKeycloakAuthorization,
  startConversationUrlParam
} from './app.config'
import { Message } from './model/messages'
import { themes } from './theme/theme.js'
import debounce from 'lodash.debounce'

export default {
  name: 'App',
  components: {
    Launcher,
    FeedbackWindow
  },
  data() {
    return {
      title: vueAppTitle,
      participants: [
        {
          id: 'bot',
          name: 'Bots4You',
          imageUrl: 'img/robot-avatar-512.svg'
        }
      ], // the list of all the participant of the conversation. `name` is the user name, `id` is used to establish the author of a message, `imageUrl` is supposed to be the user avatar.
      titleImageUrl: 'b4u_logo.svg',
      newMessagesCount: 0,
      showTypingIndicator: '', // when set to a value matching the participant.id it shows the typing indicator for the specific user
      colors: {
        header: {
          bg: '#320359',
          text: '#ffffff'
        },
        launcher: {
          bg: '#320359'
        },
        messageList: {
          bg: '#ffffff'
        },
        sentMessage: {
          bg: '#F7F5F5',
          text: '#000000',
          border: '#F1852F'
        },
        receivedMessage: {
          bg: '#EAEAEA',
          text: '#1E1E1E',
          border: '#320359'
        },
        userInput: {
          bg: '#ffffff',
          text: '#320359',
          border: '#320359'
        }
      }, // specifies the color scheme for the component
      alwaysScrollToBottom: true, // when set to true always scrolls the chat to the bottom when new events are in (new message, user starts typing...)
      messageStyling: true // enables *bold* /emph/ _underline_ and such (more info at github.com/mattezza/msgdown)
    }
  },
  computed: {
    messageList() {
      if (this.$store.state.showSystemMessages) {
        return this.$store.state.socket.messageList
      }
      return this.$store.state.socket.messageList.filter(
        message => !message.isSystemMessage()
      )
    },
    isChatOpen() {
      return this.$store.state.isChatOpen
    },
    isConversationStarted() {
      return this.$store.state.isStartConversation
    },
    isAuthorized() {
      // const parentUrl = this.$store.state.parentUrl
      return true // parentOriginHost.includes(parentUrl)
    },
    isIframeOptionEnabled() {
      return enableIframeOption === '0'
    }
  },
  beforeCreate() {
    let themeToSet = 'Default'
    let queryTheme = this.$router.options.base.split('/').pop()
    if (queryTheme) {
      themeToSet = queryTheme
    }
    themes.forEach(element => {
      if (element.name === themeToSet) {
        const name = element.name
        const dark = element.dark
        const light = element.light
        Object.keys(dark).forEach(i => {
          this.$vuetify.theme.themes.dark[i] = dark[i]
        })
        Object.keys(light).forEach(i => {
          this.$vuetify.theme.themes.light[i] = light[i]
        })
        this.$vuetify.theme.themes.name = name
      }
    })
  },
  mounted() {
    if (disableKeycloakAuthorization !== '1' && this.$keycloak.authenticated) {
      this.$store.dispatch(actions.LOG_IN)
    }
    this.$vuetify.rtl = this.$store.state.language === 'ar'

    if (startConversationUrlParam === 'false')
      this.$store.dispatch(actions.CLOSE_CONVERSATION)

    this.addMessageHandler()
  },
  destroyed() {
    this.removeMessageHandler()
  },
  methods: {
    debounceStartConversation: debounce(async function() {
      await this.$store
        .dispatch(actions.START_CONVERSATION)
        .then(() => this.$store.commit(mutations.OPEN_CHAT))
    }, 500),
    addMessageHandler() {
      window.addEventListener('message', this.postMessageConfigListener)
    },
    removeMessageHandler() {
      window.removeEventListener('message', this.postMessageConfigListener)
    },
    applyConfig(config) {
      let root = document.documentElement
      Object.keys(config).forEach(k => {
        root.style.setProperty(`--${k}`, config[k])
        if (k === 'userInputStrokeColor') {
          root.style.setProperty(`--v-primary-base`, config[k])
          root.style.setProperty(`--v-error-base`, config[k])
        }
      })
      this.$store.dispatch(actions.SET_CONFIG, config)
    },
    postMessageConfigListener: function(event) {
      if (event.data.type === 'setConfig') {
        this.applyConfig(event.data.config)
      } else if (event.data.type === 'setDocumentViewConfig') {
        this.$store.dispatch(
          actions.SET_DOCUMENT_VIEW_CONFIG,
          event.data.config
        )

        if (event.data.config.copilot_chat) {
          this.$store.dispatch(
            actions.SET_COPILOT_CHAT,
            event.data.config.copilot_chat
          )
        }

        if (event.data.config.read_only_mode !== undefined) {
          this.$store.dispatch(
            actions.SET_READ_ONLY_MODE,
            event.data.config.read_only_mode
          )
        }
      } else if (event.data.type === 'setCopilotChatTheme') {
        this.$store.dispatch(actions.SET_THEME, event.data.config)
        this.$store.dispatch(actions.SET_COPILOT_THEME, event.data.config)
      } else if (event.data.type === 'setCopilotAiConfig') {
        this.$store.dispatch(actions.SET_COPILOT_AI_CONFIG, event.data.config)
      } else if (event.data.type === 'changeCopilotAiConfig') {
        this.$store.dispatch(actions.SET_COPILOT_AI_CONFIG, event.data.config)

        if (event.data.config) {
          const slot_message = {
            message: `/slot_set{"smartdoc_is_multidoc":"${event.data.config.smartdoc_is_multidoc}","smartdoc_use_sources":"${event.data.config.smartdoc_use_sources}","smartdoc_temperature":"${event.data.config.smartdoc_temperature}","smartdoc_llm_model":"${event.data.config.smartdoc_llm_model}","smartdoc_selected_llm_model":"${event.data.config.smartdoc_selected_llm_model}"}`,
            metadata: {},
            session_id: this.$store.getters.sessionId
          }
          this.$store.dispatch(actions.EMIT_USER_SLOT_SET_MESSAGE, slot_message)
        }
      } else if (event.data.type === 'documentChanged') {
        this.$store.dispatch(
          actions.SET_CURRENT_DOCUMENT_ID,
          event.data.documentId
        )
      } else if (event.data.type === 'isMessageLimitReached') {
        this.$store.dispatch(
          actions.IS_MESSAGE_LIMIT_REACHED,
          event.data.messageLimitReached
        )
      } else if (event.data.type === 'setHideInitialTypingIndicator') {
        this.$store.dispatch(
          actions.SET_HIDE_INITIAL_TYPING_INDICATOR,
          event.data.hideInitialTypingIndicator
        )
      } else if (event.data.type === 'aiTextManipulation') {
        let message = new Message('me', event.data.senderId, event.data.query)
        message.metadata['document_sent_prompt'] = true
        this.$store.dispatch(actions.EMIT_NEW_USER_MESSAGE, message)
      } else if (event.data.type === 'click') {
        if (event.data.message === 'open') {
          sessionStorage.setItem('chatState', 'open')
          this.debounceStartConversation()
        } else if (event.data.message === 'close') {
          sessionStorage.setItem('chatState', 'closed')
          this.$store.dispatch(actions.CLOSE_CHAT)
        } else if (event.data.message === 'accepted_invitation') {
          sessionStorage.setItem('chatState', 'open')
          sessionStorage.setItem('chatInvitation', 'accepted')
          this.debounceStartConversation()
          this.$store.dispatch(
            actions.ACCEPTED_INVITATION,
            'chat_invitation_accept'
          )
        } else if (event.data.message === 'declined_invitation') {
          sessionStorage.setItem('chatInvitation', 'declined')
          this.$store.dispatch(
            actions.DECLINED_INVITATION,
            'chat_invitation_decline'
          )
        } else if (event.data.message === 'clicked_trigger_button') {
          sessionStorage.setItem('chatState', 'open')
          this.debounceStartConversation()
          this.$store.dispatch(actions.CLICKED_TRIGGER_BUTTON, 'trigger_button')
        }
      } else if (event.data.type === 'set_device_info') {
        if (event.data.device) {
          const data = {
            device: event.data.device,
            type: 'impression'
          }
          this.$store.dispatch(actions.SET_DEVICE_INFO, data)
        }
      } else if (event.data.type === 'promptsUpdated') {
        if (event.data.prompts) {
          this.$store.dispatch(actions.SET_PROMPTS, event.data.prompts)
        }
        if (event.data.promptFolders) {
          this.$store.dispatch(actions.SET_PROMPT_FOLDERS, event.data.promptFolders)
        }
      } else if (event.data.type === 'setAnalyticsContext') {
        this.$store.commit(mutations.SET_ANALYTICS_CONTEXT, event.data.context)
      }
    },
    openChat() {
      // called when the user clicks on the fab button to open the chat
      this.$store.dispatch(actions.OPEN_CHAT)
      this.newMessagesCount = 0
    },
    closeChat() {
      // called when the user clicks on the button to close the chat
      this.$store.dispatch(actions.CLOSE_CHAT)
    },

    debouncedRestartChat: debounce(function() {
      this.$store.dispatch(actions.EMIT_USER_RESTART_MESSAGE)
    }, 500),
    restartMessenger() {
      this.$store.dispatch(actions.CLEAR_MESSAGES)
      this.$store.dispatch(actions.ENABLE_CHAT_BOT, false)
      this.$store.dispatch(actions.ENABLE_LIVE_CHAT, false)
    },
    goBack() {
      //const latestMessage = this.$store.getters.latestMessage.text

      /*if (botFirstMessage.includes(latestMessage)) {
        return false
      }*/

      const quickReply = {
        title: this.$t('back'),
        payload: '/base.back'
      }

      this.$log.info(`Emitting QuickReply with payload: ${quickReply.payload}`)

      const message = new Message(
        'me',
        this.$store.getters.sessionId,
        quickReply.title ? quickReply.title : quickReply.payload
      )

      message.payload = quickReply.payload

      this.$store.dispatch(actions.SET_BACK_INPUT_DISABLED, true)
      this.$store.dispatch(actions.EMIT_NEW_USER_MESSAGE, message)
    },
    handleScrollToTop() {
      // called when the user scrolls message list to top
      // leverage pagination for loading another page of messages
    },
    handleOnType() {}
  }
}
</script>

<style lang="scss">
@import './assets/variables.css';

@font-face {
  font-family: 'Dax';
  src: url(assets/fonts/3A8C94_B_0.woff2) format('woff2');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Dax';
  src: url(assets/fonts/3A8C94_7_0.woff2) format('woff2');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Dax';
  src: url(assets/fonts/3A8C94_8_0.woff2) format('woff2');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}
html {
  overflow: hidden !important;
}
#app {
  font-family: 'Dax', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
.v-application--wrap {
  min-height: 0 !important;
}
.bg {
  background: #f3f4f5;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-size: cover;
  transform: scale(1.1);
}
.v-window__prev,
.v-window__next {
  background: rgba(125, 125, 125, 0.3);
}
.v-icon.fas.fa-chevron-left,
.v-icon.fas.fa-chevron-right {
  color: white;
  font-size: 24px !important;
}
#notfound {
  position: relative;
  height: 100vh;
  background-color: #222;
}

#notfound .notfound {
  position: absolute;
  left: 50%;
  top: 50%;
  -webkit-transform: translate(-50%, -50%);
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
}

.notfound {
  max-width: 460px;
  width: 100%;
  text-align: center;
  line-height: 1;
}

.notfound .notfound-401 h1 {
  color: #222;
  font-size: 10rem;
  letter-spacing: 20px;
  margin: 0;
  font-weight: 700;
  text-shadow: 2px 2px 0 #c9c9c9, -2px -2px 0 #c9c9c9;
}

.notfound .notfound-401 h1 > span {
  text-shadow: 2px 2px 0 #ffab00, -2px -2px 0 #ffab00, 0 0px 8px #ff8700;
}

.notfound .notfound-401 h2 {
  color: #ffab00;
  font-size: 2.5rem;
  text-transform: uppercase;
  letter-spacing: 5px;
}

.notfound p {
  color: #c9c9c9;
  font-size: 16px;
  font-weight: 400;
  margin-top: 20px;
  line-height: 25px;
}

.notfound p a {
  font-size: 14px;
  text-decoration: underline;
  background: transparent;
  color: #c9c9c9;
  display: inline-block;
  font-weight: 700;
  -webkit-transition: 0.2s all;
  transition: 0.2s all;
}

.notfound p a:hover {
  color: #ffab00;
}

@media only screen and (max-width: 480px) {
  .notfound .notfound-401 h1 {
    font-size: 122px;
  }
}

pre {
  overflow-x: auto;
  background-color: rgba(0, 0, 0, 0.17);
}

.v-application code {
  background-color: unset;
  color: var(--botMessageTextColor);
}
</style>
